import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Check, Crown, Heart, Sparkles, Zap, Star, ArrowRight } from 'lucide-react';
import axios from 'axios';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [boosts, setBoosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    fetchPackages();
    fetchCurrentSubscription();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/packages`
      );
      setPackages(response.data.packages);
      setBoosts(response.data.boosts);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleSubscribe = async (packageId) => {
    try {
      console.log('🎯 Starting subscription process for package:', packageId);
      
      const token = localStorage.getItem('token');
      console.log('🔑 Auth token check:', token ? `Token found (length: ${token.length})` : 'No token found');
      
      if (!token) {
        console.log('❌ No auth token found, redirecting to login');
        navigate('/login');
        return;
      }

      // TEST: Verify token is valid by calling /me endpoint first
      try {
        const meResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Token validation successful, user:', meResponse.data);
      } catch (authError) {
        console.error('❌ Token validation failed:', authError.response?.status);
        console.error('❌ Auth error details:', authError.response?.data);
        alert('Your login session has expired. Please log in again.');
        navigate('/login');
        return;
      }

      console.log('📤 Creating checkout session...');
      
      // Use dynamic URLs that match the actual deployment domain
      const origin = window.location.origin;
      const successUrl = `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}/subscription/plans`;
      
      console.log('🌐 Using dynamic URLs for domain:', origin);
      console.log('✅ Success URL:', successUrl);
      console.log('✅ Cancel URL:', cancelUrl);
      
      const requestData = {
        package_id: packageId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        metadata: {
          source: 'web_subscription',
          package_id: packageId
        }
      };
      
      console.log('📋 Request data:', JSON.stringify(requestData, null, 2));
      console.log('🔗 API URL:', `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`,
        requestData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Checkout session created:', response.data);
      console.log('📊 Response status:', response.status);
      
      if (response.data.checkout_url) {
        console.log('🚀 Redirecting to Stripe checkout:', response.data.checkout_url);
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        console.error('❌ No checkout URL received in response');
        console.error('❌ Full response:', response.data);
        alert('Payment setup failed. No checkout URL received. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error creating checkout session:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error config:', error.config);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('❌ Final error message:', errorMessage);
      
      alert(`Upgrade failed: ${errorMessage}`);
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'premium':
        return <Sparkles className="w-6 h-6" />;
      case 'vip':
        return <Crown className="w-6 h-6" />;
      default:
        return <Heart className="w-6 h-6" />;
    }
  };

  const getPlanColor = (tier) => {
    switch (tier) {
      case 'premium':
        return 'from-rose-500 to-pink-500';
      case 'vip':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const isCurrentPlan = (packageId) => {
    return currentSubscription?.subscription?.tier === packageId.split('_')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center overflow-x-hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-4">
              <Heart className="w-12 h-12 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl font-bold text-white mb-6">
            Unlock Your 
            <span className="bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> Perfect Match</span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Choose the plan that's right for your dating journey. From AI-powered connections to VIP experiences, 
            we have everything you need to find meaningful relationships.
          </p>

          {currentSubscription?.is_premium && (
            <div className="inline-flex items-center bg-green-500/20 border border-green-500/30 rounded-full px-6 py-2 mb-8">
              <Crown className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-green-400 font-medium">
                You're currently subscribed to {currentSubscription.subscription?.tier} plan
              </span>
            </div>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.package_id}
              className={`relative bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-300 ${
                pkg.tier === 'premium' ? 'transform scale-105 border-rose-500/50' : ''
              }`}
            >
              {pkg.tier === 'premium' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-r ${getPlanColor(pkg.tier)} flex items-center justify-center mb-4`}>
                  {getPlanIcon(pkg.tier)}
                </div>
                
                <CardTitle className="text-2xl font-bold text-white mb-2">
                  {pkg.name}
                </CardTitle>
                
                <div className="text-center">
                  <span className="text-4xl font-bold text-white">${pkg.price}</span>
                  <span className="text-gray-400">/{pkg.interval}</span>
                </div>
                
                <p className="text-gray-300 text-sm mt-2">{pkg.description}</p>
              </CardHeader>

              <CardContent className="pt-4">
                <div className="space-y-3 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => handleSubscribe(pkg.package_id)}
                  disabled={isCurrentPlan(pkg.package_id)}
                  className={`w-full py-3 text-lg font-semibold transition-all duration-300 ${
                    isCurrentPlan(pkg.package_id)
                      ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      : pkg.tier === 'premium'
                      ? 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white shadow-lg hover:shadow-rose-500/25'
                      : pkg.tier === 'vip'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-purple-500/25'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  {isCurrentPlan(pkg.package_id) ? (
                    'Current Plan'
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Boost Features */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Instant <span className="text-rose-400">Boosts</span>
          </h2>
          <p className="text-gray-300 text-lg">
            Get immediate advantages with our premium boosts
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {Object.entries(boosts).map(([boostType, boost]) => (
            <Card key={boostType} className="bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-full w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2">{boost.name}</h3>
                <p className="text-gray-300 text-sm mb-4">{boost.description}</p>
                
                <div className="text-2xl font-bold text-white mb-4">${boost.price}</div>
                
                <Button
                  onClick={() => handleBoostPurchase(boostType)}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
                >
                  Buy Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-white mb-8">
            Questions? We've got answers
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="text-left">
              <h4 className="text-lg font-semibold text-white mb-2">Can I cancel anytime?</h4>
              <p className="text-gray-300">Yes! You can cancel your subscription at any time with no cancellation fees.</p>
            </div>
            
            <div className="text-left">
              <h4 className="text-lg font-semibold text-white mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-300">We accept all major credit cards and PayPal through our secure payment processor.</p>
            </div>
            
            <div className="text-left">
              <h4 className="text-lg font-semibold text-white mb-2">Is my data secure?</h4>
              <p className="text-gray-300">Absolutely. We use industry-standard encryption to protect your personal information.</p>
            </div>
            
            <div className="text-left">
              <h4 className="text-lg font-semibold text-white mb-2">Do you offer refunds?</h4>
              <p className="text-gray-300">We offer a 7-day money-back guarantee if you're not satisfied with our service.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  async function handleBoostPurchase(boostType) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const origin = window.location.origin;
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/boost`,
        {
          boost_type: boostType,
          success_url: `${origin}/boost/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/subscription/plans`,
          metadata: {
            source: 'web_boost',
            boost_type: boostType
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating boost checkout:', error);
    }
  }
};

export default SubscriptionPlans;